import React from 'react';
import { SIGN_GRID, CELL_SIZE, CHART_SIZE, DEFAULT_SIGN_ABBREVS } from './chartConstants';

// South Indian chart: 4x4 grid, 12 outer cells (fixed signs), center 2x2 for title
// Signs are fixed in position (clockwise from top-left):
// Row 0: Pisces, Aries, Taurus, Gemini
// Row 1: Aquarius, [center], [center], Cancer
// Row 2: Capricorn, [center], [center], Leo
// Row 3: Sagittarius, Scorpio, Libra, Virgo

const SouthIndianChart = ({ chartData, title = 'Rasi Chakra', isNavamsa = false, loc = null }) => {
  if (!chartData) return null;

  const SIGN_ABBREVS = (loc && loc.signAbbrevs) || DEFAULT_SIGN_ABBREVS;
  const ascLabel = (loc && loc.ascAbbrev) || 'Asc';
  const { ascendant, planets } = chartData;

  // Group planets by their sign (rasi or navamsa)
  const planetsBySign = {};
  for (let i = 0; i < 12; i++) planetsBySign[i] = [];

  // Place ascendant
  const ascSign = isNavamsa ? ascendant.navamsa_sign : ascendant.sign_index;
  planetsBySign[ascSign].push({
    ...ascendant,
    _displaySign: ascSign,
  });

  // Place planets
  planets.forEach(p => {
    const sign = isNavamsa ? p.navamsa_sign : p.sign_index;
    planetsBySign[sign].push({
      ...p,
      _displaySign: sign,
    });
  });

  // Ascendant sign for diagonal marker
  const lagnaSign = ascendant.sign_index;

  return (
    <div style={{ textAlign: 'center' }}>
      <svg
        viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
        width="100%"
        style={{ maxWidth: 420, border: '2px solid #333', background: '#fffef5' }}
      >
        {/* Draw grid lines */}
        {/* Outer border */}
        <rect x="0" y="0" width={CHART_SIZE} height={CHART_SIZE} fill="none" stroke="#333" strokeWidth="2" />

        {/* Vertical lines */}
        <line x1={CELL_SIZE} y1={0} x2={CELL_SIZE} y2={CHART_SIZE} stroke="#333" strokeWidth="1" />
        <line x1={CELL_SIZE * 2} y1={0} x2={CELL_SIZE * 2} y2={CELL_SIZE} stroke="#333" strokeWidth="1" />
        <line x1={CELL_SIZE * 3} y1={0} x2={CELL_SIZE * 3} y2={CHART_SIZE} stroke="#333" strokeWidth="1" />
        <line x1={CELL_SIZE * 2} y1={CELL_SIZE * 3} x2={CELL_SIZE * 2} y2={CHART_SIZE} stroke="#333" strokeWidth="1" />

        {/* Horizontal lines */}
        <line x1={0} y1={CELL_SIZE} x2={CHART_SIZE} y2={CELL_SIZE} stroke="#333" strokeWidth="1" />
        <line x1={0} y1={CELL_SIZE * 2} x2={CELL_SIZE} y2={CELL_SIZE * 2} stroke="#333" strokeWidth="1" />
        <line x1={CELL_SIZE * 3} y1={CELL_SIZE * 2} x2={CHART_SIZE} y2={CELL_SIZE * 2} stroke="#333" strokeWidth="1" />
        <line x1={0} y1={CELL_SIZE * 3} x2={CHART_SIZE} y2={CELL_SIZE * 3} stroke="#333" strokeWidth="1" />

        {/* Center box borders */}
        <rect x={CELL_SIZE} y={CELL_SIZE} width={CELL_SIZE * 2} height={CELL_SIZE * 2} fill="none" stroke="#333" strokeWidth="1" />

        {/* Center title */}
        <text
          x={CELL_SIZE * 2}
          y={CELL_SIZE * 1.85}
          textAnchor="middle"
          fontSize="16"
          fontWeight="bold"
          fill="#333"
        >
          {title}
        </text>

        {/* Render each sign cell */}
        {SIGN_GRID.map((pos, signIdx) => {
          const x = pos.col * CELL_SIZE;
          const y = pos.row * CELL_SIZE;
          const cellPlanets = planetsBySign[signIdx] || [];
          const isLagna = signIdx === lagnaSign && !isNavamsa;

          return (
            <g key={signIdx}>
              {/* Lagna marker: diagonal line in ascendant's sign */}
              {isLagna && (
                <line
                  x1={x}
                  y1={y + CELL_SIZE}
                  x2={x + 20}
                  y2={y + CELL_SIZE - 20}
                  stroke="#c62828"
                  strokeWidth="2"
                />
              )}

              {/* Sign abbreviation (top-left corner) */}
              <text
                x={x + 4}
                y={y + 12}
                fontSize="11"
                fill="#888"
                fontWeight="500"
              >
                {SIGN_ABBREVS[signIdx]}
              </text>

              {/* Planets in this cell */}
              {cellPlanets.map((planet, i) => {
                const isAsc = planet.planet === 'Ascendant';
                const label = isAsc
                  ? ascLabel
                  : `${planet.abbrev || planet.planet?.substring(0, 2) || ''}${planet.is_retrograde ? '(R)' : ''}`;

                // Guard for missing degree/nakshatra (e.g., divisional chart data)
                const hasDegree = planet.degree != null && planet.nakshatra;
                const detail = hasDegree ? `${planet.degree}\u00B0${String(planet.minutes).padStart(2, '0')}'` : '';
                const nakPada = hasDegree ? `${planet.nakshatra.substring(0, 4)}-${planet.pada}` : '';

                // Position planets within the cell, stacking vertically
                const startY = y + 22;
                const lineHeight = cellPlanets.length > 4 ? 16 : 19;
                const py = startY + i * lineHeight;

                // If too many planets, use smaller font
                const fontSize = cellPlanets.length > 4 ? 9.5 : 11.5;

                return (
                  <g key={planet.planet}>
                    <text
                      x={x + 5}
                      y={py}
                      fontSize={fontSize}
                      fill={isAsc ? '#c62828' : '#1a237e'}
                      fontWeight="600"
                    >
                      {label}
                    </text>
                    {detail && (
                      <text
                        x={x + 38}
                        y={py}
                        fontSize={fontSize - 1}
                        fill="#555"
                      >
                        {detail}
                      </text>
                    )}
                    {cellPlanets.length <= 4 && nakPada && (
                      <text
                        x={x + 68}
                        y={py}
                        fontSize={fontSize - 2}
                        fill="#777"
                      >
                        {nakPada}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default SouthIndianChart;
